import classnames from 'classnames';

import style from './ProfilePage.module.scss';

import type { Nullable } from 'chaika-common-types';
import { ContactPersonData, EhrTypeOfWork } from 'chaika-frontend-types/frontend-cms';
import { EhrIdentityDocType, Locale, PrimaryChannel } from 'chaika-enums';

import { InputMask } from '@components/Input';
import { AddFieldButton } from '@components/ActionButtons/AddFieldButton';
import { SaveButton } from '@components/ActionButtons/SaveButton';
import { SelectionButtons, SelectionButtonsModifiers } from '@components/SelectionButtons';
import { DefaultOption, OptionType, Select } from '@components/Select';
import {
    EHR_FIELD_NAMES,
    getPlaceOfWorkTitle,
    identityDocTypeOptions,
    typeOfWorkOptions,
} from '@patient/store/ehr/constants';
import { Checkbox } from '@components/Checkbox';

import { BirthDateInput, DateInput, ProfileInput } from './ProfileInput';
import { ContactPersonList } from './ContactPersonList';
import { AddContactPersonCard } from './ContactPersonList/ContactPersonCard';
import { RepresentativeList } from './RepresentativeList';
import { RepresentativeCardType } from './RepresentativeList/RepresentativeCard';
import { EhrFiles } from './EhrFiles';
import { EhrPageAttributes } from 'chaika-test-attributes';
import { Button } from '@components/Button';
import { translate } from '@chaika/i18n/helper';
import { useAuthUserRole } from '@store/AuthUser/hooks/useAuthUserRole';

export interface ProfilePageParams {}

interface Props extends ProfilePageParams {
    isOnLoad: boolean;
    sex: string;
    primaryChannel?: Nullable<PrimaryChannel>;
    typeOfWork?: EhrTypeOfWork;
    isOnAddContactPerson: boolean;
    isPersonalAreaCreated: boolean;
    isDocumentsSigned: boolean;
    hasConfidants: boolean;
    hasSimilarEhrs: boolean;
    legalDocConfirm: boolean;
    extraDocConfirm: boolean;
    identityDocType?: EhrIdentityDocType;
    onCheckboxChange(event: React.ChangeEvent<HTMLInputElement>): void;
    onAddContactPerson(params: ContactPersonData): void;
    onSexChange(sex: string): void;
    onPrimaryChannelChange(params: Nullable<OptionType<PrimaryChannel>>): void;
    onTypeOfWorkChange(params: OptionType<EhrTypeOfWork>): void;
    onIdentityDocTypeChange(params: OptionType<EhrIdentityDocType>): void;
    onAddContactPersonButtonClick(): void;
    onSendRegistrationUrlClick(): void;
    onSendConfirmationCodeClick(): void;
    onCancelAddContactPerson(): void;
    onPrintIC(): void;
}

export const EHR_CONTENT_ID = 'ehrContent';

export function ProfilePage({
    isOnLoad,
    sex,
    primaryChannel,
    onPrimaryChannelChange,
    typeOfWork,
    onSexChange,
    identityDocType,
    isOnAddContactPerson,
    isDocumentsSigned,
    hasSimilarEhrs,
    legalDocConfirm,
    extraDocConfirm,
    isPersonalAreaCreated,
    hasConfidants,
    onCheckboxChange,
    onAddContactPerson,
    onAddContactPersonButtonClick,
    onIdentityDocTypeChange,
    onCancelAddContactPerson,
    onTypeOfWorkChange,
    onSendRegistrationUrlClick,
    onSendConfirmationCodeClick,
    onPrintIC,
}: Props) {
    const { hasRootAccess } = useAuthUserRole();
    const options = [
        {
            value: 'm',
            label: translate('maleShort'),
        },
        {
            value: 'f',
            label: translate('femaleShort'),
        },
    ];
    const tPrintIC = translate('printIC');

    // @ts-ignore
    return (
        !isOnLoad && (
            <div
                id={EHR_CONTENT_ID}
                className={classnames([style.root])}
            >
                {hasSimilarEhrs && <div className={style.hasSimilarEhrs}>{translate('hasDublicateCardMayBe')}</div>}
                <div className={style.contactPersonTitle}>{translate('personalInformation')}</div>
                <div className={style.column}>
                    <div className={style.doubleColumnBlock}>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                name={'lastName'}
                                title={EHR_FIELD_NAMES.lastName()}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                name={'engLastName'}
                                title={EHR_FIELD_NAMES.engLastName()}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                translationToLang={Locale.Am}
                                name={'lastName'}
                                title={translate('lastNameInArmenian')}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                translationToLang={Locale.Ge}
                                name={'lastName'}
                                title={translate('lastNameInGeorgian')}
                            />
                        </div>
                    </div>
                    <div className={style.doubleColumnBlock}>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                name={'firstName'}
                                title={EHR_FIELD_NAMES.firstName()}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                name={'engFirstName'}
                                title={EHR_FIELD_NAMES.engFirstName()}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                translationToLang={Locale.Am}
                                name={'firstName'}
                                title={translate('nameInArmenian')}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                translationToLang={Locale.Ge}
                                name={'firstName'}
                                title={translate('nameInGeorgian')}
                            />
                        </div>
                    </div>
                    <div className={style.doubleColumnBlock}>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                name={'patronymic'}
                                title={EHR_FIELD_NAMES.patronymic()}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                name={'engPatronymic'}
                                title={EHR_FIELD_NAMES.engPatronymic()}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                translationToLang={Locale.Am}
                                name={'patronymic'}
                                title={translate('patronymicInArmenian')}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                translationToLang={Locale.Ge}
                                name={'patronymic'}
                                title={translate('patronymicInGeorgian')}
                            />
                        </div>
                    </div>
                    <div className={style.doubleColumnBlock}>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                name={'oldLastName'}
                                title={EHR_FIELD_NAMES.oldLastName()}
                            />
                        </div>
                    </div>
                </div>
                <div className={style.doubleColumnBlock}>
                    <div className={style.shortInputWrapper}>
                        <BirthDateInput
                            title={EHR_FIELD_NAMES.birthDate()}
                            name={'birthDate'}
                        />
                    </div>
                    <div className={style.shortInputWrapper}>
                        <div className={style.ageInputWrapper}>
                            <ProfileInput
                                name={'age'}
                                title={translate('age')}
                                disabled={true}
                            />
                        </div>
                        <div className={style.sexSelectWrapper}>
                            <SelectionButtons
                                dataTest={EhrPageAttributes.Sex}
                                selectedValue={sex}
                                values={options}
                                modifiers={[SelectionButtonsModifiers.FullHeight]}
                                onSelect={onSexChange}
                            />
                        </div>
                    </div>
                </div>
                <div className={style.inputWrapper}>
                    <ProfileInput
                        textarea
                        name={'comment'}
                        title={EHR_FIELD_NAMES.comment()}
                    />
                </div>
                <div className={classnames([style.doubleColumnBlock, style.inputWrapper])}>
                    <Checkbox
                        dataTest={EhrPageAttributes.PersonalAccountCreated}
                        label={translate('isPersonalAreaCreated')}
                        isChecked={isPersonalAreaCreated}
                        name={'isPersonalAreaCreated'}
                        onChange={onCheckboxChange}
                    />
                    <Checkbox
                        dataTest={EhrPageAttributes.DocumentsSigned}
                        label={translate('isDocumentsSigned')}
                        isChecked={isDocumentsSigned}
                        name={'isDocumentsSigned'}
                        onChange={onCheckboxChange}
                    />
                </div>
                {!isPersonalAreaCreated && (
                    <div className={style.doubleColumnBlock}>
                        <div className={style.shortInputWrapper}>
                            <SaveButton
                                dataTest={EhrPageAttributes.SendRegistrationUrlButton}
                                label={translate('sendLinkRegisterPA')}
                                onSaveClick={onSendRegistrationUrlClick}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <SaveButton
                                dataTest={EhrPageAttributes.SendConfirmationCodeButton}
                                label={translate('sendVerificationCode')}
                                onSaveClick={onSendConfirmationCodeClick}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <Button
                                dataTest={EhrPageAttributes.PrintInformedConsent}
                                label={tPrintIC}
                                onClick={onPrintIC}
                            />
                        </div>
                    </div>
                )}
                <div className={style.contactPersonTitle}>{translate('contactDetails')}</div>
                <div className={style.doubleColumnBlock}>
                    <div className={style.shortInputWrapper}>
                        <ProfileInput
                            name={'phone'}
                            type={'tel'}
                            title={EHR_FIELD_NAMES.phone()}
                            isImportant={true}
                        />
                    </div>
                    <div className={style.shortInputWrapper}>
                        <ProfileInput
                            name={'email'}
                            type={'email'}
                            title={EHR_FIELD_NAMES.email()}
                            validIfNotEmpty={true}
                            isImportant={true}
                        />
                    </div>
                    <div className={style.shortInputWrapper}>
                        <ProfileInput
                            name={'telegram'}
                            disabled={!hasRootAccess}
                            title={EHR_FIELD_NAMES.telegram()}
                        />
                    </div>
                    <div className={style.shortInputWrapper}>
                        <ProfileInput
                            disabled={!hasRootAccess}
                            name={'telegramName'}
                            title={EHR_FIELD_NAMES.telegramName()}
                        />
                    </div>
                    <div className={style.shortInputWrapper}>
                        <ProfileInput
                            name={'whatsApp'}
                            title={EHR_FIELD_NAMES.whatsApp()}
                        />
                    </div>
                </div>
                <div className={style.contactPersonTitle}>{translate('files')}</div>
                <div className={style.contactPersonCards}>
                    <EhrFiles />
                </div>
                <div className={style.contactPersonTitle}>{translate('legalDoc')}</div>
                <div className={classnames([style.doubleColumnBlock, style.inputWrapper])}>
                    <Checkbox
                        disabled
                        dataTest={EhrPageAttributes.LegalDocConfirm}
                        label={translate('legalDocConfirm')}
                        isChecked={legalDocConfirm}
                        name={'legalDocConfirm'}
                        onChange={() => {}}
                    />
                    <Checkbox
                        disabled
                        dataTest={EhrPageAttributes.ExtraDocConfirm}
                        label={translate('extraDocConfirm')}
                        isChecked={extraDocConfirm}
                        name={'extraDocConfirm'}
                        onChange={() => {}}
                    />
                </div>
                <div className={style.doubleColumnBlock}>
                    <div className={style.shortInputWrapper}>
                        <ProfileInput
                            name={'snils'}
                            mask={InputMask.SNILS}
                            title={EHR_FIELD_NAMES.snils()}
                            validIfNotEmpty={true}
                        />
                    </div>
                    <div className={style.shortInputWrapper}>
                        <ProfileInput
                            name={'inn'}
                            mask={InputMask.INN}
                            title={EHR_FIELD_NAMES.inn()}
                            validIfNotEmpty={true}
                        />
                    </div>
                </div>
                <div className={style.column}>
                    <p>{translate('paaportOrBirth')}</p>
                    <div className={style.doubleColumnBlock}>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                name={'identityDoc'}
                                title={EHR_FIELD_NAMES.identityDoc()}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                name={'identityDocCode'}
                                title={EHR_FIELD_NAMES.identityDocCode()}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <DateInput
                                isClearable
                                noopDate
                                name={'identityDocDate'}
                                dataTest={EhrPageAttributes.IdentityDocDate}
                                maxDate={new Date()}
                                title={EHR_FIELD_NAMES.identityDocDate()}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <Select
                                dataTest={EhrPageAttributes.IdentityDocType}
                                title={EHR_FIELD_NAMES.identityDocType()}
                                defaultOptionType={DefaultOption.NotSelectedOptionMasculine}
                                options={identityDocTypeOptions()}
                                selectedValue={identityDocType}
                                onChange={onIdentityDocTypeChange}
                            />
                        </div>
                    </div>
                    <div className={style.inputWrapper}>
                        <ProfileInput
                            name={'identityDocIssuer'}
                            title={EHR_FIELD_NAMES.identityDocIssuer()}
                        />
                    </div>
                    <div className={style.inputWrapper}>
                        <ProfileInput
                            name={'identityDocIssuerTransliteration'}
                            title={EHR_FIELD_NAMES.identityDocIssuerTransliteration()}
                        />
                    </div>
                </div>
                <div className={style.column}>
                    <p>{translate('internationalPassport')}</p>
                    <div className={style.doubleColumnBlock}>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                name={'internationalIdNumber'}
                                title={EHR_FIELD_NAMES.internationalIdNumber()}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <ProfileInput
                                name={'internationalIdIssuingStateCode'}
                                title={EHR_FIELD_NAMES.internationalIdIssuingStateCode()}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <DateInput
                                noopDate
                                isClearable
                                dataTest={EhrPageAttributes.InternationalIdIssueDate}
                                name={'internationalIdIssueDate'}
                                maxDate={new Date()}
                                title={EHR_FIELD_NAMES.internationalIdIssueDate()}
                            />
                        </div>
                        <div className={style.shortInputWrapper}>
                            <DateInput
                                noopDate
                                isClearable
                                dataTest={EhrPageAttributes.InternationalIdExpiryDate}
                                name={'internationalIdExpiryDate'}
                                title={EHR_FIELD_NAMES.internationalIdExpiryDate()}
                            />
                        </div>
                    </div>
                    <div className={style.inputWrapper}>
                        <ProfileInput
                            name={'internationalIdAuthority'}
                            title={EHR_FIELD_NAMES.internationalIdAuthority()}
                        />
                    </div>
                    <div className={style.inputWrapper}>
                        <ProfileInput
                            name={'internationalIdAuthorityTransliteration'}
                            title={EHR_FIELD_NAMES.internationalIdAuthorityTransliteration()}
                        />
                    </div>
                </div>
                <div className={style.column}>
                    <div className={style.inputWrapper}>
                        <ProfileInput
                            name={'registrationAddress'}
                            title={EHR_FIELD_NAMES.registrationAddress()}
                        />
                    </div>
                    <div className={style.inputWrapper}>
                        <ProfileInput
                            name={'registrationAddressTransliteration'}
                            title={EHR_FIELD_NAMES.registrationAddressTransliteration()}
                        />
                    </div>
                    <div className={style.inputWrapper}>
                        <ProfileInput
                            name={'address'}
                            title={EHR_FIELD_NAMES.address()}
                        />
                    </div>
                    <div className={style.inputWrapper}>
                        <ProfileInput
                            name={'addressTransliteration'}
                            title={EHR_FIELD_NAMES.addressTransliteration()}
                        />
                    </div>
                    <div className={style.shortInputWrapper}>
                        <Select
                            dataTest={EhrPageAttributes.TypeOfWorkSelect}
                            title={EHR_FIELD_NAMES.typeOfWork()}
                            options={typeOfWorkOptions()}
                            selectedValue={typeOfWork}
                            onChange={onTypeOfWorkChange}
                        />
                    </div>
                    <div className={style.inputWrapper}>
                        <ProfileInput
                            name={'placeOfWork'}
                            title={getPlaceOfWorkTitle(typeOfWork)}
                        />
                    </div>
                </div>
                <div className={style.contactPersonTitle}>{translate('legalRepresentative')}</div>
                <div className={style.contactPersonCards}>
                    <RepresentativeList cardType={RepresentativeCardType.Representative} />
                </div>
                {hasConfidants && (
                    <>
                        <div className={style.contactPersonTitle}>{translate('trustees')}</div>
                        <div className={style.contactPersonCards}>
                            <RepresentativeList cardType={RepresentativeCardType.Confidant} />
                        </div>
                    </>
                )}
                <div className={style.contactPersonTitle}>{translate('contactFaces')}</div>
                <div className={style.contactPersonCards}>
                    <ContactPersonList />
                </div>
                {isOnAddContactPerson ? (
                    <AddContactPersonCard
                        onAddContactPerson={onAddContactPerson}
                        onCancelClick={onCancelAddContactPerson}
                    />
                ) : (
                    <div className={style.inputWrapper}>
                        <AddFieldButton
                            dataTest={EhrPageAttributes.ContactPersonAdd}
                            onAddClick={onAddContactPersonButtonClick}
                        />
                    </div>
                )}
            </div>
        )
    );
}
